// A render-time error boundary for MODAL content.
//
// WHY THIS EXISTS. On 2026-08-25 clicking "Generation details" on an ad that
// had failed before vision QC ran took the ENTIRE app to a blank white
// screen. The immediate cause was one unguarded `images.length` on a
// receipt-only generation record (fixed separately), but the reason a single
// bad field could blank the whole product is that nothing catches a render
// throw above these modals. React unmounts the whole tree on an uncaught
// render error, and there was no boundary between that modal and the root.
//
// So this is the CLASS fix, not the instance fix. The inspector renders
// deeply optional data assembled from several Mongoose Mixed fields — the
// exact "shape drift" hazard pages/CatalogBrowser/ErrorBoundary.tsx was
// written for, in its own words: "a malformed entry should not blank the
// entire page." The next unguarded access will now cost the user a modal
// with a readable error, not the application.
//
// NOTE: pages/CatalogBrowser/ErrorBoundary.tsx predates this and does the
// same job for that one page. It should eventually be replaced by this
// component; it is deliberately left alone here rather than refactoring a
// working page inside a fix for a different bug.

import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Box, Button, Code, Heading, Text, VStack } from '@chakra-ui/react';

type Props = {
  children: ReactNode;
  /** What the user was trying to look at, e.g. "generation details". */
  what?: string;
  /** Called when the user dismisses the failed panel — usually the modal's onClose. */
  onDismiss?: () => void;
};

type State = { error: Error | null };

export class ModalErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Full stack to the console. React strips the stack from its own log on
    // the production minified bundle, and the top frame of an uncaught render
    // error resolves to React's scheduler rather than the component that
    // threw — which cost real time to discover while chasing this crash.
    // eslint-disable-next-line no-console
    console.error('Modal render error:', error, info?.componentStack);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;
    const what = this.props.what || 'this panel';
    return (
      <Box p={6}>
        <VStack align="stretch" spacing={3}>
          <Heading size="sm" color="red.600">Could not display {what}</Heading>
          <Text fontSize="sm" color="brand.muted">
            Something in this ad's data was shaped unexpectedly, so {what} could not be
            rendered. The rest of the app is unaffected — close this and carry on, or
            try again.
          </Text>
          <Code fontSize="xs" colorScheme="red" p={3} display="block" whiteSpace="pre-wrap">
            {this.state.error.message || String(this.state.error)}
          </Code>
          <Box>
            <Button onClick={this.reset} variant="brand" size="sm" mr={2}>Try again</Button>
            {this.props.onDismiss && (
              <Button onClick={this.props.onDismiss} variant="ghost" size="sm">Close</Button>
            )}
          </Box>
        </VStack>
      </Box>
    );
  }
}
